import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { PriceProvider } from './context/PriceContext';
import Notification from './components/Notification';
import Karsilama from './pages/karsilama';
import SifreGiris from './pages/sifregiris';
import HayirliIsler from './pages/hayirliisler';
import SubeSecimi from './pages/subesecimi';
import SubeGirisi from './pages/subegirisi';
import Dashboard from './pages/dashboard';
import MagazaDurumu from './pages/magazadurumu';
import BildirimYonetimi from './pages/bildirim';
import StaffBranchCampaignPanel from './components/campaign/StaffBranchCampaignPanel';
import IpsMiniScreen from './pages/IpsMiniScreen';

// --- 1. YENİ EKLENEN İMPORTLAR (TOASTIFY) ---
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// --------------------------------------------

const FinanceUiPreview = process.env.NODE_ENV !== 'production'
  ? require('./pages/finans/FinanceUiPreview').default
  : () => null;

function AuthRoute({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsSignedIn(!!user);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  if (!authReady) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: "'Roboto', sans-serif"
      }}>
        <p style={{
          fontSize: '16px',
          color: '#4b5563',
          fontWeight: 'bold',
          padding: '10px 20px',
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
        }}>
          Oturum kontrol ediliyor...
        </p>
      </div>
    );
  }

  return isSignedIn ? children : <Navigate to="/giris" replace />;
}

/** Eski müşteri talepleri URL’si kaldırıldı; yer imleri bozulmasın diye aynı sorgu ile dashboard’a yönlendir. */
function RedirectMusteriTalepleriToDashboard() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/dashboard', search }} replace />;
}

function HistoryLockGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isProtectedPath = (pathname) =>
      pathname !== '/' && pathname !== '/giris' && pathname !== '/subesecimi' && pathname !== '/finans-preview';

    const lockSession = async () => {
      if (!isProtectedPath(window.location.pathname)) return;
      localStorage.removeItem('aktifPersonel');
      try {
        await signOut(auth);
      } catch (error) {
        // Sign out failsafe: local session already removed.
      }
      navigate('/giris', { replace: true });
    };

    const onPopState = () => {
      lockSession();
    };

    const onPageShow = (event) => {
      const navEntry = performance.getEntriesByType('navigation')[0];
      const isBackForwardNav = event.persisted || navEntry?.type === 'back_forward';
      if (isBackForwardNav) {
        lockSession();
      }
    };

    window.addEventListener('popstate', onPopState);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [navigate, location.pathname]);

  return null;
}

// Admin koruması
function ProtectedRoute({ children }) {
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = doc(db, 'kullanicilar', user.uid);
        const userSnap = await getDoc(userDoc);
        setIsAdmin(userSnap.exists() && userSnap.data().rol === 'admin');
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  if (isAdmin === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: "'Roboto', sans-serif"
      }}>
        <p style={{
          fontSize: '16px',
          color: '#4b5563',
          fontWeight: 'bold',
          padding: '10px 20px',
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
        }}>
          Yükleniyor...
        </p>
      </div>
    );
  }

  return isAdmin ? children : <Navigate to="/dashboard" />;
}

function App() {
  return (
    <PriceProvider>
      <Router>
        <HistoryLockGuard />
        
        {/* --- 2. BİLDİRİM KUTUSU --- */}
        <ToastContainer 
          position="top-right" 
          autoClose={2000}
          newestOnTop
          closeOnClick
          theme="colored" 
        />
        {/* -------------------------- */}

        <Notification />
        <Routes>
          <Route path="/" element={<Karsilama />} />
          <Route path="/ips" element={<AuthRoute><IpsMiniScreen /></AuthRoute>} />
          <Route path="/giris" element={<SifreGiris />} />
          <Route path="/hayirliisler" element={<AuthRoute><HayirliIsler /></AuthRoute>} />
          <Route path="/subesecimi" element={<SubeSecimi />} />
          <Route path="/subegirisi" element={<AuthRoute><SubeGirisi /></AuthRoute>} />
          <Route path="/dashboard" element={<AuthRoute><Dashboard /></AuthRoute>} />
          <Route
            path="/musteritalepleri"
            element={
              <AuthRoute>
                <RedirectMusteriTalepleriToDashboard />
              </AuthRoute>
            }
          />
          <Route path="/kampanya-gorevleri" element={<AuthRoute><StaffBranchCampaignPanel /></AuthRoute>} />

          {/* === TÜM DASHBOARD SAYFALARI === */}
          <Route path="/yeniislem" element={<AuthRoute><Dashboard selectedModule="YENİ İŞLEM" selectedCategory="YENİ İŞLEM" /></AuthRoute>} />
          <Route path="/hizliislem" element={<AuthRoute><Dashboard selectedModule="YENİ İŞLEM" selectedCategory="HIZLI İŞLEM" /></AuthRoute>} />
          <Route path="/tumislemler" element={<AuthRoute><Dashboard selectedModule="İŞLEMLER" selectedCategory="TÜM İŞLEMLER" /></AuthRoute>} />
          
          {/* MÜŞTERİLER MODÜLÜ */}
          <Route path="/musteriekle" element={<AuthRoute><Dashboard selectedModule="MÜŞTERİLER" selectedCategory="MÜŞTERİ EKLE" /></AuthRoute>} />
          <Route path="/musterilistesi" element={<AuthRoute><Dashboard selectedModule="MÜŞTERİLER" selectedCategory="MÜŞTERİ LİSTESİ" /></AuthRoute>} />
          <Route path="/teslimatlistesi" element={<AuthRoute><Dashboard selectedModule="MÜŞTERİLER" selectedCategory="TESLİMAT LİSTESİ" /></AuthRoute>} />
          <Route path="/musteribakiyeler" element={<AuthRoute><Dashboard selectedModule="MÜŞTERİLER" selectedCategory="MÜŞTERİ BAKİYELERİ" /></AuthRoute>} />

          <Route path="/firmaekle" element={<AuthRoute><Dashboard selectedModule="FİRMALAR" selectedCategory="FİRMA EKLE" /></AuthRoute>} />
          <Route path="/firmalistesi" element={<AuthRoute><Dashboard selectedModule="FİRMALAR" selectedCategory="FİRMA LİSTESİ" /></AuthRoute>} />
          <Route path="/gunsonukasa" element={<AuthRoute><Dashboard selectedModule="KASA" selectedCategory="GÜNSONU KASA" /></AuthRoute>} />
          <Route path="/mevcutkasa" element={<AuthRoute><Dashboard selectedModule="KASA" selectedCategory="MEVCUT KASA" /></AuthRoute>} />
          <Route path="/oncekikasa" element={<AuthRoute><Dashboard selectedModule="KASA" selectedCategory="ÖNCEKİ KASA" /></AuthRoute>} />
          <Route path="/gecmiskasaara" element={<AuthRoute><Dashboard selectedModule="KASA" selectedCategory="GEÇMİŞ KASA ARA" /></AuthRoute>} />
          <Route path="/banka" element={<AuthRoute><Dashboard selectedModule="KASA" selectedCategory="BANKA" /></AuthRoute>} />

          {/* === RAPORLAR === */}
          <Route path="/gunlukraporlar" element={<AuthRoute><Dashboard selectedModule="RAPORLAR" selectedCategory="GÜNLÜK RAPORLAR" /></AuthRoute>} />
          <Route path="/haftalikraporlar" element={<AuthRoute><Dashboard selectedModule="RAPORLAR" selectedCategory="HAFTALIK RAPORLAR" /></AuthRoute>} />
          <Route path="/aylikraporlar" element={<AuthRoute><Dashboard selectedModule="RAPORLAR" selectedCategory="AYLIK RAPORLAR" /></AuthRoute>} />
          <Route path="/raporolustur" element={<AuthRoute><Dashboard selectedModule="RAPORLAR" selectedCategory="RAPOR OLUŞTUR" /></AuthRoute>} />
          {process.env.NODE_ENV !== 'production' ? (
            <Route path="/finans-preview" element={<FinanceUiPreview />} />
          ) : null}

          {/* === ADMIN SAYFALARI === */}
          <Route path="/kullaniciyonetimi" element={<AuthRoute><ProtectedRoute><Dashboard selectedModule="YÖNETİCİ" selectedCategory="KULLANICI YÖNETİMİ" /></ProtectedRoute></AuthRoute>} />
          <Route path="/subeyonetimi" element={<AuthRoute><ProtectedRoute><Dashboard selectedModule="YÖNETİCİ" selectedCategory="ŞUBE YÖNETİMİ" /></ProtectedRoute></AuthRoute>} />
          
          {/* YENİ EKLENEN: PERSONEL YÖNETİMİ */}
          <Route path="/personelyonetimi" element={<AuthRoute><ProtectedRoute><Dashboard selectedModule="YÖNETİCİ" selectedCategory="PERSONEL YÖNETİMİ" /></ProtectedRoute></AuthRoute>} />
          
          <Route path="/firmabakiyeler" element={<AuthRoute><ProtectedRoute><Dashboard selectedModule="YÖNETİCİ" selectedCategory="FİRMA BAKİYELER" /></ProtectedRoute></AuthRoute>} />
          <Route path="/ozelraporlar" element={<AuthRoute><ProtectedRoute><Dashboard selectedModule="YÖNETİCİ" selectedCategory="ÖZEL RAPORLAR" /></ProtectedRoute></AuthRoute>} />
          
          {/* MAĞAZA DURUMU */}
          <Route path="/magazadurumu" element={<AuthRoute><ProtectedRoute><MagazaDurumu /></ProtectedRoute></AuthRoute>} />

          {/* BİLDİRİM YÖNETİMİ (Admin Korumalı) */}
          <Route path="/bildirim" element={<AuthRoute><ProtectedRoute><BildirimYonetimi /></ProtectedRoute></AuthRoute>} />

          {/* WHATSAPP AYARLARI (Admin Korumalı) */}
          <Route path="/whatsappayarlari" element={<AuthRoute><ProtectedRoute><Dashboard selectedModule="YÖNETİCİ" selectedCategory="WHATSAPP AYARLARI" /></ProtectedRoute></AuthRoute>} />

          {/* === DİĞER SAYFALAR === */}
          <Route path="/subebilgileri" element={<AuthRoute><Dashboard selectedModule="ŞUBE" selectedCategory="ŞUBE BİLGİLERİ" /></AuthRoute>} />
          <Route path="/subedegistir" element={<AuthRoute><Dashboard selectedModule="ŞUBE" selectedCategory="ŞUBE DEĞİŞTİR" /></AuthRoute>} />
          <Route path="/guncelfiyatekrani" element={<AuthRoute><Dashboard selectedModule="SARRAF" selectedCategory="GÜNCEL FİYAT EKRANI" /></AuthRoute>} />
          <Route path="/referansfiyatekrani" element={<AuthRoute><Dashboard selectedModule="SARRAF" selectedCategory="REFERANS FİYAT EKRANI" /></AuthRoute>} />
          <Route path="/mobiluygulama" element={<AuthRoute><Dashboard selectedModule="SARRAF" selectedCategory="MOBİL UYGULAMA" /></AuthRoute>} />
          <Route path="/urunekle" element={<AuthRoute><Dashboard selectedModule="URUNLER" selectedCategory="URUN EKLE" /></AuthRoute>} />
          <Route path="/urunlistesi" element={<AuthRoute><Dashboard selectedModule="URUNLER" selectedCategory="URUN LİSTESİ" /></AuthRoute>} />
          <Route path="*" element={<Navigate to="/giris" replace />} />
        </Routes>
      </Router>
    </PriceProvider>
  );
}

export default App;